import React, { useState } from 'react';

const VideoPlayer = ({ 
    url, 
    title = 'Video',
    autoPlay = true,
    muted = true,
    width = '100%',
    height = '100%',
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const isDirectVideo = url?.match(/\.(mp4|webm|ogg|mov|m4v)(\?|$)/i);
    const isYouTube = url?.includes('youtube.com') || url?.includes('youtu.be');
    const isVimeo = url?.includes('vimeo.com');

    const getEmbedUrl = (url) => {
        if (!url) return '';

        // YouTube short link
        if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${videoId}?autoplay=${autoPlay ? 1 : 0}&mute=${muted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1`;
        }

        // YouTube watch link
        if (url.includes('youtube.com/watch')) {
            const videoId = url.split('v=')[1]?.split('&')[0];
            return `https://www.youtube.com/embed/${videoId}?autoplay=${autoPlay ? 1 : 0}&mute=${muted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1`;
        }

        // Vimeo
        if (url.includes('vimeo.com/')) {
            const videoId = url.split('vimeo.com/')[1].split('?')[0];
            return `https://player.vimeo.com/video/${videoId}?autoplay=${autoPlay ? 1 : 0}&muted=${muted ? 1 : 0}`;
        }

        return url;
    };

    // No URL provided
    if (!url) {
        return (
            <div style={{
                width,
                height,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#1a1a2e',
                color: '#fff',
                borderRadius: '8px',
            }}>
                <span>No video URL provided</span>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={{
                width,
                height,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#1a1a2e',
                color: '#fff',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
            }}>
                <span style={{ fontSize: '2rem', marginBottom: '12px' }}>⚠️</span>
                <p>{error}</p>
                <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        borderRadius: '6px',
                        textDecoration: 'none',
                    }}
                >
                    Open in new tab
                </a>
            </div>
        );
    }

    // Loading spinner component
    const LoadingSpinner = () => (
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #333',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
            }} />
        </div>
    );

    // Direct video files (mp4, webm, ogg, mov, m4v)
    if (isDirectVideo) {
        return (
            <div style={{ position: 'relative', width, height, backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                {isLoading && <LoadingSpinner />}
                <video
                    src={url}
                    controls
                    autoPlay={autoPlay}
                    muted={muted}
                    playsInline
                    preload="auto"
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                    }}
                    onLoadedData={() => setIsLoading(false)}
                    onCanPlay={() => setIsLoading(false)}
                    onError={(e) => {
                        setIsLoading(false);
                        setError('Unable to play this video file');
                        console.error('Video error:', e);
                    }}
                >
                    <source src={url} type="video/mp4" />
                    <source src={url} type="video/webm" />
                    <source src={url} type="video/ogg" />
                    Your browser does not support the video tag.
                </video>
            </div>
        );
    }

    // YouTube and Vimeo use iframe
    if (isYouTube || isVimeo) {
        return (
            <div style={{ position: 'relative', width, height, backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                {isLoading && <LoadingSpinner />}
                <iframe
                    src={getEmbedUrl(url)}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={title}
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                        setIsLoading(false);
                        setError('Unable to load video');
                    }}
                />
            </div>
        );
    }

    // Unknown format - try iframe as fallback
    return (
        <div style={{ position: 'relative', width, height, backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
            {isLoading && <LoadingSpinner />}
            <iframe
                src={url}
                style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                    setIsLoading(false);
                    setError('Unable to load video');
                }}
            />
        </div>
    );
};

export default VideoPlayer;